import React, { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faCheck,
  faClock,
  faEnvelope,
  faLocationDot,
  faPaperPlane,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import emailjs from "emailjs-com";

import image from "../../images/shiwanshu.jpeg";
import "./Contact.css";

const EMAILJS_CONFIG = {
  serviceId:
    process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_ystlmqj",
  templateId:
    process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_pwxep1n",
  publicKey:
    process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "efnQ_-lOOJQ0YcFtY",
};

const PROCESS_STEPS = [
  "We review your goals and current challenges",
  "You receive a practical next-step recommendation",
  "We define the right scope, team, and delivery approach",
];

const PROJECT_TYPES = [
  "Custom software development",
  "Web application",
  "Mobile application",
  "Product modernization",
  "Automation or AI solution",
  "Technology consultation",
  "Other",
];

function Contact() {
  const formRef = useRef(null);
  const [submission, setSubmission] = useState({
    status: "idle",
    message: "",
  });

  const isSubmitting = submission.status === "submitting";

  const sendEmail = async (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    const honeypot = form.elements.website?.value;

    if (honeypot) {
      form.reset();
      return;
    }

    setSubmission({
      status: "submitting",
      message: "Sending your project enquiry…",
    });

    try {
      await emailjs.sendForm(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        form,
        EMAILJS_CONFIG.publicKey,
      );

      form.reset();
      setSubmission({
        status: "success",
        message:
          "Thank you. Your enquiry has been received, and our team will respond within one business day.",
      });
    } catch {
      setSubmission({
        status: "error",
        message:
          "We couldn’t send your enquiry right now. Please try again or contact us directly by WhatsApp or email.",
      });
    }
  };

  return (
    <main className="contact-page">
      <div className="contact-page__decoration" aria-hidden="true" />

      <section
        className="contact-page__container"
        aria-labelledby="contact-page-title"
      >
        <div className="contact-page__introduction">
          <p className="contact-page__eyebrow">
            <span aria-hidden="true" />
            Start a conversation
          </p>

          <h1 id="contact-page-title">
            Let’s turn your next idea into
            <span> something useful.</span>
          </h1>

          <p className="contact-page__lead">
            Tell us about the opportunity, challenge, or product you have in
            mind. We’ll review it carefully and return with a clear, practical
            way forward.
          </p>

          <ul className="contact-page__process" aria-label="What happens next">
            {PROCESS_STEPS.map((step, index) => (
              <li key={step}>
                <span aria-hidden="true">
                  <FontAwesomeIcon icon={faCheck} />
                </span>
                <div>
                  <small>Step {String(index + 1).padStart(2, "0")}</small>
                  <strong>{step}</strong>
                </div>
              </li>
            ))}
          </ul>

          <div className="contact-page__contact-details">
            <a href="mailto:contact@axiomos.in">
              <span aria-hidden="true">
                <FontAwesomeIcon icon={faEnvelope} />
              </span>
              <div>
                <small>Email us</small>
                <strong>contact@axiomos.in</strong>
              </div>
            </a>

            <a href="tel:+917738899255">
              <span aria-hidden="true">
                <FontAwesomeIcon icon={faPhone} />
              </span>
              <div>
                <small>Call us</small>
                <strong>+91 77388 99255</strong>
              </div>
            </a>

            <div>
              <span aria-hidden="true">
                <FontAwesomeIcon icon={faLocationDot} />
              </span>
              <div>
                <small>Based in</small>
                <strong>Pune, India</strong>
              </div>
            </div>
          </div>

          <aside className="contact-page__profile" aria-label="Your Axiomos contact">
            <div className="contact-page__profile-image">
              <img
                src={image}
                alt="Shiwanshu Pandey, Founder of Axiomos Technologies LLP"
                loading="eager"
                decoding="async"
              />
              <span aria-hidden="true" />
            </div>

            <div className="contact-page__profile-copy">
              <span>Your first point of contact</span>
              <h2>Shiwanshu Pandey</h2>
              <p>Founder, Axiomos Technologies LLP</p>
            </div>

            <a
              href="https://wa.me/917738899255?text=Hi%20Shiwanshu%2C%20I%27d%20like%20to%20discuss%20a%20project%20with%20Axiomos."
              target="_blank"
              rel="noopener noreferrer"
              className="contact-page__whatsapp"
            >
              WhatsApp
              <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </a>
          </aside>
        </div>

        <div className="contact-page__form-card">
          <div className="contact-page__form-heading">
            <div>
              <p>Project enquiry</p>
              <h2>Tell us what you’re building.</h2>
            </div>
            <span aria-hidden="true">AX / 01</span>
          </div>

          <form ref={formRef} onSubmit={sendEmail} noValidate={false}>
            <div className="contact-page__form-grid">
              <div className="contact-page__field">
                <label htmlFor="contact-name">
                  Your name <span aria-hidden="true">*</span>
                </label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  placeholder="Full name"
                  autoComplete="name"
                  maxLength="100"
                  required
                />
              </div>

              <div className="contact-page__field">
                <label htmlFor="contact-email">
                  Work email <span aria-hidden="true">*</span>
                </label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  placeholder="name@company.com"
                  autoComplete="email"
                  maxLength="160"
                  required
                />
              </div>

              <div className="contact-page__field">
                <label htmlFor="contact-phone">Phone / WhatsApp</label>
                <input
                  type="tel"
                  id="contact-phone"
                  name="phone"
                  placeholder="+91 98765 43210"
                  autoComplete="tel"
                  inputMode="tel"
                  maxLength="24"
                />
              </div>

              <div className="contact-page__field">
                <label htmlFor="contact-project-type">Project type</label>
                <select id="contact-project-type" name="project_type" defaultValue="">
                  <option value="" disabled>
                    Select a service
                  </option>
                  {PROJECT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="contact-page__field contact-page__field--full">
                <label htmlFor="contact-about">
                  About your project <span aria-hidden="true">*</span>
                </label>
                <textarea
                  id="contact-about"
                  name="about"
                  rows="6"
                  placeholder="What are you looking to build, improve, or solve? Include any useful timeline or business context."
                  maxLength="3000"
                  required
                />
                <small>Share as much context as you can. Up to 3,000 characters.</small>
              </div>
            </div>

            <div className="contact-page__honeypot" aria-hidden="true">
              <label htmlFor="contact-website">Website</label>
              <input
                type="text"
                id="contact-website"
                name="website"
                tabIndex="-1"
                autoComplete="off"
              />
            </div>

            <label className="contact-page__consent">
              <input type="checkbox" name="privacy_consent" value="accepted" required />
              <span aria-hidden="true" />
              <small>
                I agree that Axiomos may use these details to respond to my
                enquiry.
              </small>
            </label>

            <div className="contact-page__form-footer">
              <div className="contact-page__response-time">
                <FontAwesomeIcon icon={faClock} aria-hidden="true" />
                <span>
                  Typical response
                  <strong>Within one business day</strong>
                </span>
              </div>

              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Sending…" : "Send project enquiry"}
                <FontAwesomeIcon
                  icon={isSubmitting ? faPaperPlane : faArrowRight}
                  aria-hidden="true"
                />
              </button>
            </div>

            <div
              className={`contact-page__status${
                submission.status !== "idle"
                  ? ` is-${submission.status}`
                  : ""
              }`}
              role="status"
              aria-live="polite"
            >
              {submission.message}
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}

export default Contact;
